import React, { useState } from "react";
import useSWR from "swr";
import {
  Table, Modal, Form, Input, InputNumber, Select, message, Popconfirm, Button, Space, Drawer,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  getRiskRuleList, createRiskRule, updateRiskRule, deleteRiskRule, toggleRiskRuleUse,
  getSolutionGroupSelect,
  getRiskMappingList, createRiskMapping, deleteRiskMapping,
} from "../api/domainApi";
import { RiskRuleRes, RiskMappingRes } from "../types/domain";
import PageHeader from "../components/common/PageHeader";
import UseYnTag from "../components/common/UseYnTag";

const PAGE_SIZE = 20;
const COND_OPTS = [{ value: "AND", label: "AND — 모두 선택 시" }, { value: "OR", label: "OR — 하나라도 선택 시" }];

const RiskRulePage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [kwInput, setKwInput] = useState("");
  const [filterGrpId, setFilterGrpId] = useState<number | undefined>();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<RiskRuleRes | null>(null);
  const [form] = Form.useForm();

  const [selectedRule, setSelectedRule] = useState<RiskRuleRes | null>(null);
  const [mapDrawerOpen, setMapDrawerOpen] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [mapForm] = Form.useForm();

  const swrKey = `/risk-rule?page=${page}&kw=${keyword}&grp=${filterGrpId}`;
  const { data, isLoading, mutate } = useSWR(
    swrKey,
    () => getRiskRuleList({ page: page - 1, size: PAGE_SIZE, keyword, sltnGrpId: filterGrpId }),
    { keepPreviousData: true }
  );

  const { data: sltnGroups } = useSWR("/solution-group/select", getSolutionGroupSelect);

  const mapSwrKey = selectedRule ? `/risk-rule/${selectedRule.id}/mapping` : null;
  const { data: mappings, mutate: mutateMap } = useSWR(mapSwrKey, () => getRiskMappingList(selectedRule!.id));

  const openCreate = () => { setEditing(null); form.resetFields(); setModalOpen(true); };
  const openEdit = (row: RiskRuleRes) => { setEditing(row); form.setFieldsValue(row); setModalOpen(true); };

  const handleOk = async () => {
    const values = await form.validateFields();
    try {
      if (editing) {
        await updateRiskRule(editing.id, values);
        message.success("수정되었습니다.");
      } else {
        await createRiskRule(values);
        message.success("추가되었습니다.");
      }
      setModalOpen(false);
      mutate();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteRiskRule(id);
      message.success("삭제되었습니다.");
      mutate();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const openMapDrawer = (row: RiskRuleRes) => { setSelectedRule(row); setMapDrawerOpen(true); };
  const openMapCreate = () => { mapForm.resetFields(); setMapModalOpen(true); };

  const handleMapOk = async () => {
    const values = await mapForm.validateFields();
    try {
      await createRiskMapping(selectedRule!.id, values);
      message.success("추가되었습니다.");
      setMapModalOpen(false);
      mutateMap();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const handleMapDelete = async (mappingId: number) => {
    try {
      await deleteRiskMapping(selectedRule!.id, mappingId);
      message.success("삭제되었습니다.");
      mutateMap();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const columns: ColumnsType<RiskRuleRes> = [
    { title: "ID", dataIndex: "id", width: 70 },
    { title: "솔루션그룹", dataIndex: "sltnGrpTitle", width: 120 },
    { title: "위험코드", dataIndex: "riskCode", width: 100 },
    { title: "위험명", dataIndex: "riskName", ellipsis: { showTitle: true } },
    { title: "경고문구", dataIndex: "warningText", ellipsis: { showTitle: true } },
    { title: "매핑 수", dataIndex: "mappingCount", width: 80 },
    { title: "사용여부", dataIndex: "useYn", width: 90, render: (v) => <UseYnTag value={v} /> },
    {
      title: "관리",
      width: 220,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>수정</Button>
          <Button size="small" type="dashed" onClick={() => openMapDrawer(row)}>매핑</Button>
          <Button size="small" onClick={async () => {
            try { await toggleRiskRuleUse(row.id); mutate(); }
            catch (e: any) { message.error(e.message); }
          }}>
            {row.useYn ? "비활성" : "활성"}
          </Button>
          <Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDelete(row.id)}>
            <Button size="small" danger>삭제</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const mapColumns: ColumnsType<RiskMappingRes> = [
    { title: "매핑ID", dataIndex: "mappingId", width: 80 },
    { title: "선택지ID", dataIndex: "questionOptionId", width: 90 },
    { title: "선택지 라벨", dataIndex: "optionLabel", ellipsis: { showTitle: true } },
    { title: "조건유형", dataIndex: "conditionType", width: 80 },
    {
      title: "관리", width: 80,
      render: (_, row) => (
        <Popconfirm title="삭제?" onConfirm={() => handleMapDelete(row.mappingId)}>
          <Button size="small" danger>삭제</Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="위험규칙" onAdd={openCreate} />
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          placeholder="솔루션그룹 선택" allowClear style={{ width: 180 }}
          options={sltnGroups?.map((g) => ({ value: g.id, label: g.sltnGrpTitle }))}
          onChange={(v) => { setFilterGrpId(v); setPage(1); }}
        />
        <Input placeholder="위험명/코드 검색" value={kwInput}
          onChange={(e) => setKwInput(e.target.value)}
          onPressEnter={() => { setKeyword(kwInput); setPage(1); }}
          style={{ width: 220 }}
        />
        <Button onClick={() => { setKeyword(kwInput); setPage(1); }}>검색</Button>
        <Button onClick={() => { setKwInput(""); setKeyword(""); setFilterGrpId(undefined); setPage(1); }}>초기화</Button>
      </Space>
      <Table
        rowKey="id" columns={columns} dataSource={data?.content} loading={isLoading}
        pagination={{ current: page, pageSize: PAGE_SIZE, total: data?.page?.totalElements, onChange: setPage, showSizeChanger: false }}
        size="small"
      />

      {/* Rule Modal */}
      <Modal
        title={editing ? "위험규칙 수정" : "위험규칙 추가"}
        open={modalOpen} onOk={handleOk} onCancel={() => setModalOpen(false)}
        okText={editing ? "수정" : "추가"} cancelText="취소"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="sltnGrpId" label="솔루션 그룹" rules={[{ required: true }]}>
            <Select options={sltnGroups?.map((g) => ({ value: g.id, label: g.sltnGrpTitle }))} />
          </Form.Item>
          <Form.Item name="riskCode" label="위험 코드" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="riskName" label="위험명" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="warningText" label="경고 문구"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      {/* Mapping Drawer */}
      <Drawer
        title={`매핑 관리 — ${selectedRule?.riskName ?? ""}`}
        open={mapDrawerOpen} onClose={() => setMapDrawerOpen(false)} size={700}
        extra={<Button type="primary" onClick={openMapCreate}>매핑 추가</Button>}
      >
        <Table rowKey="mappingId" columns={mapColumns} dataSource={mappings} pagination={false} size="small" />
      </Drawer>

      {/* Mapping Modal */}
      <Modal
        title="매핑 추가" open={mapModalOpen} onOk={handleMapOk} onCancel={() => setMapModalOpen(false)}
        okText="추가" cancelText="취소"
      >
        <Form form={mapForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="questionOptionId" label="선택지 ID" rules={[{ required: true }]}>
            <InputNumber style={{ width: "100%" }} min={1} />
          </Form.Item>
          <Form.Item name="conditionType" label="조건 유형" rules={[{ required: true }]}>
            <Select options={COND_OPTS} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RiskRulePage;
